<template>
   <div :data-cy="props.id" class="py-1">
      <div class="grid grid-cols-3 gap-2">
         <NumberTextBox
            v-if="isFormatDDMMYY"
            v-model.trim="state.input.day"
            placeholder="DD"
            :hasError="showBlockErrors && inputHasError('day')"
            :label="dayLabel || $t('validation.date.day.period')"
            :maxLength="2"
            :ariaOnlyLabel="ariaOnlyLabel"
            :isDisabled="isDisabled"
            @change="processInputs"
         />
         <NumberTextBox
            v-model.trim="state.input.month"
            placeholder="MM"
            :hasError="showBlockErrors && inputHasError('month')"
            :label="monthLabel || $t('validation.date.month.period')"
            :maxLength="2"
            :ariaOnlyLabel="ariaOnlyLabel"
            :isDisabled="isDisabled"
            @change="processInputs"
         />
         <NumberTextBox
            v-if="!isFormatDDMMYY"
            v-model.trim="state.input.day"
            placeholder="DD"
            :hasError="showBlockErrors && inputHasError('day')"
            :label="dayLabel || $t('validation.date.day.period')"
            :maxLength="2"
            :ariaOnlyLabel="ariaOnlyLabel"
            :isDisabled="isDisabled"
            @change="processInputs"
         />
         <NumberTextBox
            v-model.trim="state.input.year"
            placeholder="YYYY"
            :hasError="showBlockErrors && inputHasError('year')"
            :label="yearLabel || $t('validation.date.year.period')"
            :maxLength="4"
            :ariaOnlyLabel="ariaOnlyLabel"
            :isDisabled="isDisabled"
            @change="processInputs"
         />
      </div>
      <InvalidInputsList
         aria-live="polite"
         :errorList="errorList"
         :class="{ hidden: !showBlockErrors }"
         class="-pl-3 bg-white"
      />
   </div>
</template>

<script setup lang="ts">
/* Block for capturing a date */
import useVuelidate from "@vuelidate/core";
import { required, numeric, maxValue, minValue, minLength } from "@vuelidate/validators";
import { useI18n } from "@/vue-i18n/vue-i18n";
import { isDateReal } from "@/components/flowsel/date/date-utils";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

export interface dateInputsEmits {
   id: string;
   valid: boolean;
   year?: string;
   month?: string;
   day?: string;
}

dayjs.extend(customParseFormat);

const emit = defineEmits(["inputs-update"]);

const { t: $t } = useI18n();

// props
// -----------------------------------------
interface Props {
   id: string;
   isFormatDDMMYY: boolean;
   ariaOnlyLabel?: boolean;
   dayLabel?: string;
   monthLabel?: string;
   yearLabel?: string;
   isRequired: boolean;
   validations?: {
      min?: string;
      max?: string;
   };
   showBlockErrors: boolean;
   existingAnswer: {
      year: string | null;
      month: string | null;
      day: string | null;
   };
   isDisabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
   isDisabled: false,
});

// Data
// -----------------------------------------
const state = reactive({
   input: {
      day: "",
      month: "",
      year: "",
   },
});

// Vuelidate & validation
// This component handles all single-date-specific validations.
// Extra validations (e.g. for a date range) may be applied to a parent component
const getErrorMessage = (validator, property) => {
   if (validator === "required" && props.isRequired) {
      return $t("validation.date.required");
   } else if (validator === "numeric") {
      return $t("validation.date.invalid_type");
   } else if (validator === "minValue" || validator === "maxValue") {
      const errorKey = validator === "minValue" ? "invalid_min" : "invalid_max";
      return $t(`validation.date.${property}.${errorKey}`);
   } else if (validator === "minLength" && property === "year") {
      return $t("validation.date.year.invalid_length");
   } else if (validator === "outOfDateRange") {
      // This error will be slightly different depending whether a min, max, or both have been set for this date
      const thisInputMax = props.validations?.max;
      const thisInputMin = props.validations?.min;
      const readableInputMax =
         thisInputMax === "today" ? $t(`validation.date.today`) : dayjs(thisInputMax).format("D MMM YYYY");
      const readableInputMin =
         thisInputMin === "today" ? $t(`validation.date.today`) : dayjs(thisInputMin).format("D MMM YYYY");
      // Show the min &/ max in the error, in an easily understandable format
      if (thisInputMax && thisInputMin) {
         return $t("validation.date.range.out_of_range_both", { min: readableInputMin, max: readableInputMax });
      } else if (thisInputMax) {
         return $t("validation.date.range.out_of_range_max", { date: readableInputMax });
      } else {
         return $t("validation.date.range.out_of_range_min", { date: readableInputMin });
      }
   } else if (validator === "realDate") {
      return $t("validation.date.invalid_date");
   } else return null;
};

const outOfDateRange = () => {
   //    console.log("checking date range for");
   let max: null | Date = null;
   let min: null | Date = null;
   // Because we are not asking about time of day, set 'time' on all dates to 00:00:00 - this means
   // that our 'today' value will match other date values where we specified D, M & Y but not time
   const todayNow = new Date();
   const todayMidnight = new Date(todayNow.getFullYear(), todayNow.getMonth(), todayNow.getDate(), 0, 0, 0);
   // Set min and max dates where they exist for this input (date or endDate)
   if (props.validations?.max) {
      // Either a date or 'today' may be set as a min or max value
      if (props.validations.max === "today") max = todayMidnight;
      else max = new Date(`${props.validations.max}T00:00:00`);
   }
   if (props.validations?.min) {
      if (props.validations.min === "today") min = todayMidnight;
      else min = new Date(`${props.validations.min}T00:00:00`);
   }

   //    console.log("MIN", min, "MAX", max);
   // If no min or max is set, return true
   if (!max && !min) return true;
   // if all fields are empty, return true (to allow for optional questions)
   if (!state.input.day && !state.input.month && !state.input.year) return true;

   // Otherwise, convert the current date value to a timestamp to compare
   const currentDate = new Date(
      parseInt(state.input.year),
      parseInt(state.input.month) - 1,
      parseInt(state.input.day),
      0,
      0,
      0
   );
   // However if any field is filled and the date is invalid, currentDate.getTime will be NaN and we'll return false
   const result = currentDate.getTime() >= (min as Date).getTime() && currentDate.getTime() <= (max as Date).getTime();
   //    console.log("---------", currentDate, result);
   return result;
};

const rules = {
   input: {
      day: {
         ...(props.isRequired && { required }),
         numeric,
         maxValue: maxValue(31),
         minValue: minValue(1),
         $autoDirty: true,
      },
      month: {
         ...(props.isRequired && { required }),
         numeric,
         maxValue: maxValue(12),
         minValue: minValue(1),
         $autoDirty: true,
      },
      year: {
         ...(props.isRequired && { required }),
         numeric,
         minLength: minLength(4),
         outOfDateRange,
         $autoDirty: true,
      },
      date: {
         realDate: () => isDateReal(state.input.day, state.input.month, state.input.year),
         $autoDirty: true,
      },
   },
};

const $v: any = useVuelidate(rules, state);

// computed
// -----------------------------------------

const errorList = computed(() => {
   const dateErrors = $v.value.input.$errors.map((vError) => {
      return {
         property: vError.$property,
         inputId: props.id,
         validator: vError.$validator,
         message: getErrorMessage(vError.$validator, vError.$property),
      };
   });
   // only show 1 error at a time
   return dateErrors.slice(0, 1);
});

// Methods
// -----------------------------------------
// update the inputs with any existing answer data and validate
const initialiseComponent = function () {
   if (props.existingAnswer.year && props.existingAnswer.month && props.existingAnswer.day) {
      state.input = props.existingAnswer;
   }
   processInputs();
};

const inputHasError = (inputKey) => {
   const propertiesWithErrors = errorList.value.map((item) => item.property);
   return propertiesWithErrors.includes(inputKey);
};

function processInputs() {
   if (!$v.value.$dirty) $v.value.$touch();

   emit("inputs-update", {
      id: props.id,
      valid: !$v.value.$invalid && !$v.value.$error,
      year: state.input.year,
      month: state.input.month,
      day: state.input.day,
   });
}

// On load, check for an existing answer or for user data
initialiseComponent();
</script>
